import { SystemType } from '@agilelab/plugin-wb-builder-common';
import {
  RbacApi,
  PermissionCheckResult,
  CheckRolePermissionCheck,
} from '@agilelab/plugin-wb-rbac-common';
import { witboostTeamRoles, witboostTeamRolesByRelation } from './teamRoles';
import { stringifyEntityRef } from '@backstage/catalog-model';

type TeamRoleConfigAssigneeKind = 'user' | 'group';

export type TeamRoleConfig = {
  assigneeRbacRole: string;
  assigneeKind?: TeamRoleConfigAssigneeKind;
  limitedAssigneeRbacRole?: string;
  limitedAssigneeKind?: TeamRoleConfigAssigneeKind;
};

/**
 * Needed for restricting Team Roles kind to user until group support is implemented
 * @param systemType
 * @param relation
 * @returns
 */
export const parseTeamRoleConfigFromSystemTypeRelation = (
  systemType: SystemType,
  relation: string,
): TeamRoleConfig => {
  if (!systemType.spec?.[relation]) {
    throw new Error(
      `The SystemType (${stringifyEntityRef(
        systemType,
      )}) does not have the "${relation}" field in its spec, which is required for parsing the Team Role configuration.`,
    );
  }

  const teamRoleConfig = systemType.spec[relation] as TeamRoleConfig;

  // set assigneeKind and limitedAssigneeKind to 'user' if not specified, since 'group' support is not implemented yet
  const defaultAssigneeKind: TeamRoleConfigAssigneeKind = 'user';
  const defaultLimitedAssigneeKind: TeamRoleConfigAssigneeKind = 'user';

  return {
    assigneeRbacRole: teamRoleConfig.assigneeRbacRole,
    assigneeKind: teamRoleConfig.assigneeKind ?? defaultAssigneeKind,
    limitedAssigneeRbacRole: teamRoleConfig.limitedAssigneeRbacRole,
    limitedAssigneeKind:
      teamRoleConfig.limitedAssigneeKind ?? defaultLimitedAssigneeKind,
  };
};

export const safeParseTeamRoleConfigFromSystemTypeRelation = (
  systemType: SystemType,
  relation: string,
): TeamRoleConfig | null => {
  try {
    return parseTeamRoleConfigFromSystemTypeRelation(systemType, relation);
  } catch (error) {
    return null;
  }
};

/**
 * Verifies that all RBAC roles referenced in the SystemType Team Roles spec section exist in the RBAC plugin.
 * If at least one of the referenced RBAC roles does not exist, an error is thrown.
 * @param systemType
 * @param rbacApi
 * @param options - RBAC scoped credentials
 */
export const throwIfInvalidTeamRolesConfig = async (
  systemType: SystemType,
  rbacApi: RbacApi,
  options: { token: string },
) => {
  const errorMessages: string[] = [];

  const specFields: string[] = [
    ...witboostTeamRoles.map(role => role.practiceShaperRelation),
  ];

  const existingSpecFields: string[] = specFields.filter(
    field => systemType.spec[field],
  );

  if (existingSpecFields.length === 0) {
    return;
  }

  // Retrieve existing rbac roles
  const rbacChecks: CheckRolePermissionCheck[] = existingSpecFields.flatMap(
    practiceShaperTeamRoleRelation => {
      const teamRoleConfig = systemType.spec[
        practiceShaperTeamRoleRelation
      ] as TeamRoleConfig;

      if (
        teamRoleConfig.assigneeRbacRole ===
        teamRoleConfig.limitedAssigneeRbacRole
      ) {
        errorMessages.push(
          `The assigneeRbacRole and limitedAssigneeRbacRole cannot be the same ("${
            teamRoleConfig.assigneeRbacRole
          }") for the "${practiceShaperTeamRoleRelation}" Team Role of SystemType (${stringifyEntityRef(
            systemType,
          )}). Please provide different RBAC roles for assigneeRbacRole and limitedAssigneeRbacRole.`,
        );
        return [];
      }

      return [
        [
          {
            roleId: teamRoleConfig.assigneeRbacRole,
            permissionIds: [
              witboostTeamRolesByRelation[practiceShaperTeamRoleRelation]
                .assigneeRbacPermission.name,
            ],
          },
        ],
        teamRoleConfig.limitedAssigneeRbacRole
          ? {
              roleId: teamRoleConfig.limitedAssigneeRbacRole,
              permissionIds: [
                witboostTeamRolesByRelation[practiceShaperTeamRoleRelation]
                  .limitedAssigneeRbacPermission.name,
              ],
            }
          : [],
      ].flat();
    },
  );

  if (rbacChecks.length === 0) {
    if (errorMessages.length > 0) {
      throw new Error(errorMessages.join(' '));
    }
    return;
  }

  const response = await rbacApi.checkRolePermissions({
    checks: rbacChecks,
    options,
  });

  const unmatchedRbacRoles = new Set<string>();
  const missingPermissionsByRole: Record<string, string[]> = {};

  response.results.forEach(roleCheck => {
    if (roleCheck.roleStatus === 'not_found') {
      unmatchedRbacRoles.add(roleCheck.roleId);
      return;
    }

    if (roleCheck.hasAllPermissions) {
      return;
    }

    const missingPermissions: string[] = roleCheck.permissionChecks
      .filter(
        (permissionCheck: PermissionCheckResult) =>
          permissionCheck.status !== 'granted',
      )
      .map(
        (permissionCheck: PermissionCheckResult) =>
          permissionCheck.permissionId,
      );

    missingPermissionsByRole[roleCheck.roleId] = missingPermissions;
  });

  if (unmatchedRbacRoles.size > 0) {
    const referencedIn = existingSpecFields.filter(field => {
      const teamRoleProps = systemType.spec[field] as TeamRoleConfig;
      return (
        unmatchedRbacRoles.has(teamRoleProps.assigneeRbacRole) ||
        unmatchedRbacRoles.has(teamRoleProps.limitedAssigneeRbacRole || '')
      );
    });

    errorMessages.push(
      `"${[...unmatchedRbacRoles].join('", "')}" RBAC ${
        unmatchedRbacRoles.size === 1 ? 'role' : 'roles'
      } related to SystemType (${stringifyEntityRef(
        systemType,
      )}), and referenced in "${referencedIn.join('", "')}", ${
        unmatchedRbacRoles.size === 1 ? 'does' : 'do'
      } not exist.`,
    );
  }

  for (const [roleId, missingPermissions] of Object.entries(
    missingPermissionsByRole,
  )) {
    const referencedIn = existingSpecFields.filter(field => {
      const teamRoleProps = systemType.spec[field] as TeamRoleConfig;
      return teamRoleProps.assigneeRbacRole === roleId;
    });

    errorMessages.push(
      `RBAC role "${roleId}" related to SystemType (${stringifyEntityRef(
        systemType,
      )}) and referenced in "${referencedIn.join(
        '", "',
      )}" is missing the following permissions: "${missingPermissions.join(
        '", "',
      )}".`,
    );
  }

  if (errorMessages.length > 0) {
    throw new Error(errorMessages.join(' '));
  }
};
